import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import TermsEditor from './TermsEditor';
import { DEFAULT_TERMS } from '../../../hooks/usePartnerships';

const { FiX, FiLoader, FiChevronRight, FiChevronLeft, FiFileText } = FiIcons;

const TYPES = [
  { value: 'co_release',    label: 'Co-Release' },
  { value: 'revenue_share', label: 'Revenue Share' },
  { value: 'tour',          label: 'Tour' },
  { value: 'merch',         label: 'Merch' },
  { value: 'label_deal',    label: 'Label Deal' },
];

const inputCls = "w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-colors";
const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-500">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
  </div>
);

const STEPS = ['Details', 'Terms', 'Review'];

const StepDot = ({ idx, current, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[10px] font-black transition-all ${
      idx < current ? 'bg-white border-white text-black'
      : idx === current ? 'border-indigo-400 text-indigo-400'
      : 'border-zinc-700 text-zinc-600'
    }`}>{idx + 1}</div>
    <span className={`text-[10px] font-black uppercase tracking-widest ${idx === current ? 'text-white' : 'text-zinc-600'}`}>{label}</span>
  </div>
);

const NewPartnershipModal = ({ artistId, onClose, onCreate }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    partner_slug: '', title: '', partnership_type: 'co_release',
    description: '', revenue_split: 50,
    terms: '', start_date: new Date().toISOString().split('T')[0],
  });
  const [termsData, setTermsData] = useState({ ...DEFAULT_TERMS });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.partner_slug.trim()) { setErr('Partner slug is required.'); return false; }
      if (!form.title.trim()) { setErr('Partnership title is required.'); return false; }
    }
    setErr('');
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => Math.min(s + 1, 2)); };
  const back = () => { setErr(''); setStep(s => Math.max(s - 1, 0)); };

  const handleSubmit = async () => {
    setLoading(true);
    setErr('');
    const { error } = await onCreate({
      ...form,
      initiator_artist_id: artistId,
      terms_structured: termsData,
    });
    setLoading(false);
    if (error) setErr(error.message);
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-8 py-6 border-b border-zinc-900">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">New</p>
              <h2 className="text-2xl font-black text-white uppercase tracking-tight">Partnership Request</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 transition-all">
              <SafeIcon icon={FiX} className="text-lg" />
            </button>
          </div>
          {/* Step indicators */}
          <div className="flex items-center gap-4">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <StepDot idx={i} current={step} label={label} />
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-white' : 'bg-zinc-800'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="p-8 space-y-6 max-h-[62vh] overflow-y-auto">
          {/* ── Step 0: Details ── */}
          {step === 0 && (
            <div className="space-y-5">
              <Field label="Partner Artist Slug" required>
                <input className={inputCls} placeholder="e.g. nova-drift" value={form.partner_slug}
                  onChange={e => set('partner_slug', e.target.value)} />
              </Field>
              <Field label="Partnership Title" required>
                <input className={inputCls} placeholder="e.g. Summer Tour 2025" value={form.title}
                  onChange={e => set('title', e.target.value)} />
              </Field>
              <Field label="Type">
                <div className="grid grid-cols-2 gap-2">
                  {TYPES.map(t => (
                    <button key={t.value} type="button" onClick={() => set('partnership_type', t.value)}
                      className={`px-4 py-3 rounded-xl border text-xs font-black uppercase tracking-widest transition-all ${
                        form.partnership_type === t.value
                          ? 'bg-white text-black border-white'
                          : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300'
                      }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Description">
                <textarea className={`${inputCls} resize-none`} rows={3}
                  placeholder="What is this partnership about?"
                  value={form.description} onChange={e => set('description', e.target.value)} />
              </Field>
              <Field label="Start Date">
                <input type="date" className={inputCls} value={form.start_date}
                  onChange={e => set('start_date', e.target.value)} />
              </Field>
            </div>
          )}

          {/* ── Step 1: Terms ── */}
          {step === 1 && (
            <TermsEditor
              terms={termsData}
              onChange={setTermsData}
              showRevSplit={form.partnership_type === 'revenue_share'}
              revSplit={form.revenue_split}
              onRevSplitChange={v => set('revenue_split', v)}
            />
          )}

          {/* ── Step 2: Review ── */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="p-5 bg-zinc-900/50 border border-zinc-800 rounded-2xl space-y-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Partnership Summary</p>
                <h3 className="text-xl font-black text-white uppercase tracking-tight">{form.title}</h3>
                <div className="flex flex-wrap gap-2 text-[10px] font-black uppercase tracking-widest text-zinc-400">
                  <span>Partner: <span className="text-white">{form.partner_slug}</span></span>
                  <span>·</span>
                  <span>Type: <span className="text-white">{form.partnership_type.replace('_', ' ')}</span></span>
                  <span>·</span>
                  <span>Starts: <span className="text-white">{form.start_date}</span></span>
                </div>
                {form.description && (
                  <p className="text-sm font-bold text-zinc-400 leading-relaxed">{form.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiFileText} className="text-zinc-600 text-sm" />
                  <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Terms Overview</p>
                </div>
                <div className="p-4 bg-zinc-900/40 border border-zinc-800 rounded-2xl space-y-2 text-[10px] font-black uppercase tracking-widest">
                  {[
                    ['Payment', termsData.payment_schedule?.replace('_', ' ')],
                    ['Exclusivity', termsData.exclusivity?.replace('_', ' ')],
                    ['Territory', termsData.territory?.replace('_', ' ')],
                    ['Content Rights', termsData.content_rights?.replace('_', ' ')],
                    ['Notice', `${termsData.cancellation_notice_days} days`],
                    ['Auto-Renew', termsData.auto_renew ? 'Yes' : 'No'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-zinc-600">{k}</span>
                      <span className="text-zinc-300">{v}</span>
                    </div>
                  ))}
                  {(termsData.custom_clauses || []).length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Custom Clauses</span>
                      <span className="text-zinc-300">{termsData.custom_clauses.length}</span>
                    </div>
                  )}
                </div>
              </div>

              <Field label="Free-Form Terms Notes (optional)">
                <textarea className={`${inputCls} resize-none`} rows={3}
                  placeholder="Any additional notes or conditions..."
                  value={form.terms} onChange={e => set('terms', e.target.value)} />
              </Field>
            </div>
          )}

          {err && <p className="text-red-400 text-xs font-black">{err}</p>}
        </div>

        {/* Footer nav */}
        <div className="px-8 py-5 border-t border-zinc-900 flex items-center justify-between gap-3">
          {step > 0 ? (
            <button onClick={back}
              className="flex items-center gap-2 px-5 py-3 border border-zinc-800 rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:border-zinc-600 hover:text-white transition-all">
              <SafeIcon icon={FiChevronLeft} className="text-sm" /> Back
            </button>
          ) : <div />}

          {step < 2 ? (
            <button onClick={next}
              className="flex items-center gap-2 px-6 py-3 bg-white text-black text-xs font-black uppercase tracking-widest rounded-2xl hover:scale-105 active:scale-95 transition-all">
              Next <SafeIcon icon={FiChevronRight} className="text-sm" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white text-xs font-black uppercase tracking-widest rounded-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50">
              {loading && <SafeIcon icon={FiLoader} className="animate-spin" />}
              Send Partnership Request
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewPartnershipModal;